import { memo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Form, Formik, type FormikValues } from "formik";
import { faSearch } from "@fortawesome/pro-solid-svg-icons/faSearch";
import { SubmitOnChange } from "@edgetier/components";
import type { IProps } from "./filters.types";
import { ResetButton } from "./components/reset-button";
import { FilterFields } from "./components/filter-fields";
import { Button } from "../../button";
import styles from "./filters.module.scss";
import { Box } from "../../box";
import classNames from "classnames";

/**
 * Component for managing custom filters in a form.
 * @param {object[]} props.filters - The array of filter configurations.
 * @param {function} props.onSubmit - Function to handle form submission.
 * @param {object} props.initialValues - The initial values for the form fields.
 * @param {boolean} props.submitOnChange - Flag to submit the form on change.
 * @param {boolean} props.syncWithUrl - Flag to synchronize with URL parameters.
 * @param {boolean} props.isResetEnabled - Flag to enable reset button.
 * @param {string[]} props.visibleFilters - The visible filter IDs.
 * @param {string[]} props.fixedFilters - The fixed filter IDs.
 * @param {string[]} props.transformValues - Transformation function to transform values before being submitted or synced with URL.
 * @returns {JSX.Element} Filters component.
 */
const Filters = <FormType extends FormikValues>({
    activable,
    filters,
    onSubmit,
    initialValues,
    submitOnChange,
    syncWithUrl,
    isResetEnabled = submitOnChange !== true,
    visibleFilters: visibleFiltersProp,
    fixedFilters,
    transformValues,
    groupIcons,
}: IProps<FormType>) => {
    const [, setSearch] = useSearchParams();

    const onSubmitHandler = useCallback(
        (values: FormType) => {
            const valuesToSubmit = transformValues ? transformValues(values) : values;
            if (syncWithUrl) {
                setSearch(valuesToSubmit);
            }
            onSubmit && onSubmit(valuesToSubmit);
        },
        [onSubmit, syncWithUrl, setSearch, transformValues]
    );

    return (
        <Formik<FormType> onSubmit={onSubmitHandler} initialValues={initialValues} enableReinitialize>
            <Form data-testid="custom-filters" className={styles["custom-filters"]}>
                <FilterFields
                    activable={activable}
                    filters={filters}
                    visibleFilters={visibleFiltersProp}
                    fixedFilters={fixedFilters}
                    groupIcons={groupIcons}
                >
                    {submitOnChange && <SubmitOnChange />}
                    {(!submitOnChange || isResetEnabled) && (
                        <Box className={classNames("custom-filters__buttons", styles["buttons"])}>
                            {!submitOnChange && (
                                <Button
                                    data-testid={"filters-search-button"}
                                    className={styles["submit"]}
                                    icon={faSearch}
                                    type="submit"
                                >
                                    Search
                                </Button>
                            )}
                            {isResetEnabled && <ResetButton />}
                        </Box>
                    )}
                </FilterFields>
            </Form>
        </Formik>
    );
};

export default memo(Filters);
